import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  ListItem,
  Select,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { TextareaWithVariableInsert } from "@/components/textarea-with-variable-insert/TextareaWithVariableInsert.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  HTTPRequestHeaderValues,
  WorkflowEditActionHTTPRequestHeaderModal,
} from "@/pages/workflows/edit/actions/WorkflowEditActionHTTPRequestHeaderModal.tsx";
import { WebhookSecretModal } from "@/pages/settings/webhooks/widgets/WebhookSecretModal.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import {
  useWorkflowRotateBlockActionHTTPRequestSignatureSecret,
  useWorkflowUpdateBlockActionHTTPRequest,
} from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiWorkflowBlockActionHTTPRequest,
  ApiWorkflowBlockActionHTTPRequestSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
} from "@/pages/workflows/workflow-hook.tsx";

export interface Props extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionHTTPRequest: ApiWorkflowBlockActionHTTPRequest;
}

interface HTTPRequestHeader extends HTTPRequestHeaderValues {
  id: number;
}

type HTTPRequestFormValues = Omit<ApiWorkflowBlockActionHTTPRequestSave, "custom_headers">;

export const WorkflowEditActionHTTPRequestModal = ({
  workflowVersion,
  block,
  actionHTTPRequest,
  ...props
}: Props) => {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    http_method: yup.string().required(t("errors:field_required")),
    url: yup.string().required(t("errors:field_required")),
    payload_template: yup.string().required(t("errors:field_required")),
  });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<HTTPRequestFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      http_method: actionHTTPRequest.http_method,
      url: actionHTTPRequest.url,
      payload_template: actionHTTPRequest.payload_template,
    },
  });

  const tempIdCounter = useRef(0);
  const [headers, setHeaders] = useState<HTTPRequestHeader[]>(() =>
    Object.entries(actionHTTPRequest.custom_headers ?? {}).map(([key, value], index) => ({
      id: index + 1,
      key,
      value,
    }))
  );

  const handleAddHeader = () => {
    openModal(WorkflowEditActionHTTPRequestHeaderModal, {
      existingKeys: headers.map((header) => header.key),
      eventType: workflowVersion.trigger_event?.event_type,
      onSave: (data) => {
        tempIdCounter.current -= 1;
        setHeaders((prev) => [...prev, { ...data, id: tempIdCounter.current }]);
      },
    });
  };

  const handleEditHeader = (headerToEdit: HTTPRequestHeader) => {
    openModal(WorkflowEditActionHTTPRequestHeaderModal, {
      header: headerToEdit,
      existingKeys: headers
        .filter((header) => header.id !== headerToEdit.id)
        .map((header) => header.key),
      eventType: workflowVersion.trigger_event?.event_type,
      onSave: (data) => {
        setHeaders((prev) =>
          prev.map((header) => (header.id === headerToEdit.id ? { ...header, ...data } : header))
        );
      },
    });
  };

  const handleDeleteHeader = (headerId: number) => {
    setHeaders((prev) => prev.filter((header) => header.id !== headerId));
  };

  const updateBlockActionNotification = useWorkflowUpdateBlockActionHTTPRequest(control);
  const rotateSignatureSecret = useWorkflowRotateBlockActionHTTPRequestSignatureSecret();

  const rotateSecret = async () => {
    try {
      const result = await rotateSignatureSecret.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
      });
      openModal(WebhookSecretModal, { secret: result.signature_secret });
    } catch {
      // Error handling is provided by the mutation.
    }
  };

  const handleSave = handleSubmit(async (data) => {
    try {
      const custom_headers = headers.reduce<Record<string, string>>((acc, header) => {
        acc[header.key] = header.value;
        return acc;
      }, {});

      await updateBlockActionNotification.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: { ...data, custom_headers },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.send_notification,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const handleTabChanged = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_http_request")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockActionNotification.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={handleTabChanged} activeTab={activeTab}>
            <TabItem title={t("common:message")}>
              <Stack gap={"lg"} className={"py-6"}>
                <DemoUnavailable
                  appearance="notice"
                  label={t("integrations:demo_http_workflow_title")}
                  description={t("integrations:demo_http_workflow_description")}
                />
                <Stack>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={"HTTP Method"}
                        emptyTitle={t("common:select")}
                        value={value}
                        required={true}
                        options={[
                          { id: "GET", title: "GET" },
                          { id: "POST", title: "POST" },
                          { id: "PUT", title: "PUT" },
                          { id: "PATCH", title: "PATCH" },
                          { id: "DELETE", title: "DELETE" },
                        ]}
                        onChange={onChange}
                        error={errors.http_method?.message}
                      />
                    )}
                    control={control}
                    name={"http_method"}
                  />
                  <InputWithVariableInsert
                    label={"URL"}
                    type="text"
                    placeholder="https://example.com/api/endpoint"
                    {...register("url")}
                    required={true}
                    error={errors.url?.message}
                    variableOptions={getWorkflowPlaceholderOptions(
                      workflowVersion.trigger_event?.event_type
                    )}
                    disableSpacing
                  />
                  <TextareaWithVariableInsert
                    {...register("payload_template")}
                    label={t("workflow:action_type_http_request_payload_data")}
                    required={true}
                    error={errors.payload_template?.message}
                    variableOptions={getWorkflowPlaceholderOptions(
                      workflowVersion.trigger_event?.event_type
                    )}
                    disableSpacing
                  />
                </Stack>
                <Title header={t("workflow:action_type_http_request_custom_headers")} size={"xs"} />
                {headers.length > 0 && (
                  <Stack gap={"sm"}>
                    {headers.map((header) => (
                      <ListItem
                        key={header.id}
                        behavior={"card"}
                        icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-globe"} />} />}
                        preTitle={header.key}
                        title={header.value}
                        valueSlot={
                          <DropdownMenu>
                            <DropdownMenuItem
                              title={t("common:edit")}
                              leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                              onClick={() => handleEditHeader(header)}
                            />
                            <DropdownMenuItem
                              title={t("common:delete")}
                              leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                              variant={"menuDanger"}
                              onClick={() => handleDeleteHeader(header.id)}
                            />
                          </DropdownMenu>
                        }
                      />
                    ))}
                  </Stack>
                )}
                <Stack>
                  <Button
                    size={"sm"}
                    variant={"secondary"}
                    type={"button"}
                    onClick={handleAddHeader}
                  >
                    {t("workflow:action_type_http_request_add_header")}
                  </Button>
                </Stack>
                <ListItem
                  title={t("workflow:action_type_http_request_signature")}
                  valueSlot={
                    <Button
                      size={"sm"}
                      variant={"secondary"}
                      onClick={() => void rotateSecret()}
                      isLoading={rotateSignatureSecret.isPending}
                    >
                      {t("core:webhook_rotate_secret")}
                    </Button>
                  }
                />
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />

                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
